import React from 'react';

import { Trans } from '@lingui/react/macro';
import { useAtomValue } from 'jotai';

import { PatientListModesMap } from '../../@types/appointments';
import { VideaTypography } from '../../common-components/elements/VideaTypography/VideaTypography';
import { ActionsTopbar } from '../../layout/header/topbar/ActionsTopbar';
import type {
  Appointment,
  AppointmentProvider,
} from '../../react-query/useAppointments';
import type {
  GetTaskResponse,
  TaskCounts,
} from '../../react-query/useGetTasks';
import type { TVisitSummary } from '../../react-query/useVisitSummaries';
import { useDebounce } from '../../utils/hooks/useDebounce';

import { modeAtom, searchAtom, selectDateAtom, sortFilterAtom } from './atoms';
import { FiltersSidebar } from './filtersSidebar/FiltersSidebar';
import { LoadingSpinner } from './styles';
import {
  type PatientListPageLayoutContext,
  PatientListPageLayoutContextMap,
} from './types';
import { useShouldHideHuddle } from './useShouldHideHuddle';

const NO_PATIENTS_MESSAGE_STYLES =
  'vai-w-full vai-text-center vai-p-8 vai-text-zinc-700';

interface PatientListPageLayoutProps {
  children: React.ReactNode;
  allAppointments?: Appointment[];
  appointmentIdsToTasks?: Record<string, GetTaskResponse>;
  context: PatientListPageLayoutContext;
  filteredAppointments?: Appointment[];
  nextAppointmentIndex?: number;
  providersList?: AppointmentProvider[];
  taskCounts?: TaskCounts;
  visitSummaries?: TVisitSummary[];
  hasNewAnalyses?: boolean;
  isLoadingAppointments: boolean;
  isLoadingVisitSummaries: boolean;
  isNextAppointmentInView?: boolean;
  isRefreshRequested?: boolean;
  onRefresh?: () => void;
  onShowNewAnalyses?: () => void;
  onScrollNextAppointmentIntoView?: () => void;
  onCreateReferral?: () => void;
  parentRef?: React.RefObject<HTMLDivElement>;
}

export const PatientListPageLayout = ({
  allAppointments = [],
  appointmentIdsToTasks,
  children,
  filteredAppointments = [],
  context,
  nextAppointmentIndex,
  parentRef,
  providersList,
  taskCounts,
  visitSummaries,
  hasNewAnalyses,
  isLoadingAppointments,
  isLoadingVisitSummaries,
  isNextAppointmentInView,
  isRefreshRequested,
  onRefresh,
  onShowNewAnalyses,
  onScrollNextAppointmentIntoView,
  onCreateReferral,
}: PatientListPageLayoutProps) => {
  // state
  const mode = useAtomValue(modeAtom);
  const search = useAtomValue(searchAtom);
  const selectedDate = useAtomValue(selectDateAtom);
  const sortFilter = useAtomValue(sortFilterAtom);

  // hooks
  const hideHuddle = useShouldHideHuddle();
  const isHuddle = context === PatientListPageLayoutContextMap.Huddle;
  const { debouncedValue: debouncedSearch } = useDebounce(search);

  const isSearchValid = debouncedSearch.length > 2;

  // Helper function to create no patients message
  const createNoPatientsMessage = (message: React.ReactNode) => (
    <VideaTypography
      data-testid="no-patients-message"
      variant="headingExtrasmall"
      className={NO_PATIENTS_MESSAGE_STYLES}
      as="h3"
    >
      {message}
    </VideaTypography>
  );

  // vars
  const getNoPatientsMessage = () => {
    if (isSearchValid) {
      return createNoPatientsMessage(
        <Trans>No patients found matching search parameters.</Trans>,
      );
    }

    if (context === PatientListPageLayoutContextMap.AutoVerify) {
      return createNoPatientsMessage(
        <Trans>
          No patients found matching the selected insurance status filter.
        </Trans>,
      );
    }

    if (mode === PatientListModesMap.DailyHuddle) {
      return createNoPatientsMessage(
        <Trans>No patients found for this date.</Trans>,
      );
    }

    if (sortFilter === "Today's Radiographs") {
      return createNoPatientsMessage(
        <Trans>
          No patients found for today&apos;s date. Select a different filter
          option or use the search bar to find more patients.
        </Trans>,
      );
    }

    if (selectedDate) {
      return createNoPatientsMessage(
        <Trans>No patients found for the selected date.</Trans>,
      );
    }

    return createNoPatientsMessage(
      <Trans>No patients found for this practice.</Trans>,
    );
  };

  const noPatientsMessage = getNoPatientsMessage();

  const loadingSpinner = (
    <LoadingSpinner className="vai-m-16 vai-block vai-mx-auto vai-mb-0" />
  );

  const loadingOrEmptyState = isLoadingAppointments
    ? loadingSpinner
    : noPatientsMessage;

  return (
    <div className="vai-grid vai-grid-rows-[64px_1fr] vai-h-screen vai-w-screen vai-overflow-hidden">
      <ActionsTopbar
        appointments={allAppointments}
        appointmentIdsToTasks={appointmentIdsToTasks}
        context={context}
        visitSummaries={visitSummaries}
        nextAppointmentIndex={nextAppointmentIndex}
        hasNewAnalyses={hasNewAnalyses}
        isLoading={isLoadingAppointments}
        isNextAppointmentInView={isNextAppointmentInView}
        isRefreshRequested={isRefreshRequested}
        onRefresh={onRefresh}
        onShowNewAnalyses={onShowNewAnalyses}
        onScrollNextAppointmentIntoView={onScrollNextAppointmentIntoView}
        onCreateReferral={onCreateReferral}
      />

      <div className="vai-flex vai-bg-gray-100 vai-overflow-hidden">
        {isHuddle && !hideHuddle && (
          <FiltersSidebar
            appointments={filteredAppointments || []}
            isLoadingAppointments={isLoadingAppointments}
            isLoadingVisitSummaries={isLoadingVisitSummaries}
            providersList={providersList || []}
            taskCounts={taskCounts || ({} as TaskCounts)}
            visitSummaries={visitSummaries}
          />
        )}
        <div className="vai-flex-1 vai-flex vai-flex-col vai-overflow-hidden">
          <div
            className="vai-flex-1 vai-p-4 vai-pt-0 vai-shadow-sm vai-overflow-y-auto"
            ref={parentRef}
          >
            <div className="vai-py-3">
              {!isLoadingAppointments &&
                context === PatientListPageLayoutContextMap.Huddle && (
                  <p
                    className="vai-typography-small vai-text-zinc-700"
                    data-testid="total-appointments"
                  >
                    {allAppointments.length} <Trans>Total Appointments</Trans>
                  </p>
                )}
            </div>
            {/* patient list */}
            {/* Referrals and AutoVerify handle their own empty states */}
            {allAppointments.length === 0 &&
            context !== PatientListPageLayoutContextMap.Referrals &&
            context !== PatientListPageLayoutContextMap.AutoVerify
              ? loadingOrEmptyState
              : children}
          </div>
        </div>
      </div>
    </div>
  );
};
